import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

/**
 * 秒数を MM:SS 形式にフォーマット
 * @param seconds 秒数
 * @returns MM:SS 形式の文字列（例: "1:23", "12:05"）
 */
export const formatDuration = (seconds: number): string => {
  const d = dayjs.duration(seconds, 'seconds')
  return d.format('m:ss')
}

/**
 * 秒数を HH:MM:SS または MM:SS 形式にフォーマット
 * 1時間未満の場合は MM:SS、1時間以上の場合は HH:MM:SS を返す
 * @param seconds 秒数
 * @returns HH:MM:SS または MM:SS 形式の文字列
 */
export const formatTime = (seconds: number): string => {
  const d = dayjs.duration(seconds, 'seconds')
  return d.hours() > 0 ? d.format('HH:mm:ss') : d.format('mm:ss')
}

/**
 * 秒数を HH:MM:SS 形式にフォーマット（常に3桁）
 * @param seconds 秒数（undefined の場合は 00:00:00 を返す）
 * @returns HH:MM:SS 形式の文字列（例: "01:23:45"）
 */
export const formatSecondsToHHMMSS = (seconds: number | undefined): string => {
  if (seconds === undefined) return '00:00:00'
  return dayjs.duration(Math.ceil(seconds), 'seconds').format('HH:mm:ss')
}

/**
 * タイムスタンプを日時文字列にフォーマット
 * @param timestamp Unixタイムスタンプ（ミリ秒）
 * @returns 日時文字列（例: "2024/12/11 14:30"）
 */
export const formatDateTime = (timestamp: number): string => {
  return dayjs(timestamp).format('YYYY/MM/DD HH:mm')
}

/**
 * 分数を日本語の時間表記にフォーマット
 * @param minutes 分数
 * @returns 日本語の時間表記（例: "1分未満", "30分", "2時間15分"）
 */
export const formatMinutesJa = (minutes: number): string => {
  if (minutes < 1) return '1分未満'
  const d = dayjs.duration(minutes, 'minutes')
  const h = d.hours()
  const m = d.minutes()
  if (h === 0) return `${m}分`
  return m > 0 ? `${h}時間${m}分` : `${h}時間`
}

/**
 * 秒数を日本語の時間表記にフォーマット
 * @param seconds 秒数
 * @returns 日本語の時間表記（例: "30分", "2時間15分"）
 */
export const formatSecondsJa = (seconds: number): string => {
  const d = dayjs.duration(seconds, 'seconds')
  const h = d.hours()
  const m = d.minutes()
  if (h === 0) return `${m}分`
  return m > 0 ? `${h}時間${m}分` : `${h}時間`
}

/**
 * 秒数を短い英語形式にフォーマット（ストレス表示用）
 * @param seconds 秒数（nullの場合は '--' を返す）
 * @returns 短い英語形式の文字列（例: "30m", "2h 15m"）
 */
export const formatSecondsShort = (seconds: number | null): string => {
  if (seconds === null) return '--'
  const d = dayjs.duration(seconds, 'seconds')
  const h = d.hours()
  const m = d.minutes()
  if (h > 0) return `${h}h ${m}m`
  return `${m}m`
}

/**
 * video/* または audio/* の MIME タイプかどうかを判定
 * true の場合、num_pages / progress は秒数として扱う
 */
export const isDurationMime = (mimeType: string | null | undefined): boolean => {
  if (!mimeType) return false
  return mimeType.startsWith('video/') || mimeType.startsWith('audio/')
}
